import http from "../../api/apiClient";
import type { Catalog, ExportFormat, ReportResult, ReportSpec, SavedReport } from "./types";

const BASE = "/reports/builder";

export async function fetchCatalog(): Promise<Catalog> {
  const { data } = await http.get(`${BASE}/catalog`);
  return data;
}

/** Runs a definition that has not been saved (or has unsaved changes). Only called on "Run report". */
export async function runReport(definition: ReportSpec, page = 0, size = 50, signal?: AbortSignal): Promise<ReportResult> {
  const { data } = await http.post(`${BASE}/run`, { definition, page, size }, { signal });
  return data;
}

export async function runSavedReport(id: string, page = 0, size = 50, signal?: AbortSignal): Promise<ReportResult> {
  const { data } = await http.get(`${BASE}/definitions/${encodeURIComponent(id)}/result`, {
    params: { page, size },
    signal,
  });
  return data;
}

export async function fetchMyReports(): Promise<SavedReport[]> {
  const { data } = await http.get(`${BASE}/definitions`);
  return Array.isArray(data) ? data : [];
}

/** Reports colleagues shared in the school's library. */
export async function fetchLibrary(): Promise<SavedReport[]> {
  const { data } = await http.get(`${BASE}/library`);
  return Array.isArray(data) ? data : [];
}

export async function shareReport(id: string, shared: boolean): Promise<SavedReport> {
  const { data } = await http.put(`${BASE}/definitions/${encodeURIComponent(id)}/sharing`, { shared });
  return data;
}

/**
 * All rows (at most 10,000) for Excel or print. A saved report without changes is exported by id,
 * otherwise the definition as composed. Recorded in the audit log.
 */
export async function exportReport(request: { definition?: ReportSpec; reportId?: string; format: ExportFormat }):
  Promise<ReportResult> {
  const { data } = await http.post(`${BASE}/export`, request);
  return data;
}

export async function fetchReport(id: string): Promise<SavedReport> {
  const { data } = await http.get(`${BASE}/definitions/${encodeURIComponent(id)}`);
  return data;
}

export type SaveRequest = { name: string; description?: string | null; definition: ReportSpec };

export async function createReport(request: SaveRequest): Promise<SavedReport> {
  const { data } = await http.post(`${BASE}/definitions`, request);
  return data;
}

export async function updateReport(id: string, request: SaveRequest): Promise<SavedReport> {
  const { data } = await http.put(`${BASE}/definitions/${encodeURIComponent(id)}`, request);
  return data;
}

export async function deleteReport(id: string): Promise<void> {
  await http.delete(`${BASE}/definitions/${encodeURIComponent(id)}`);
}
